import { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Brain,
  Lightning,
  ChartLineUp,
  Truck,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const analysisTypes = [
  { key: "insights", label: "Insights", icon: Brain, color: "text-teal-400" },
  {
    key: "anomalies",
    label: "Anomalies",
    icon: Lightning,
    color: "text-amber-400",
  },
  {
    key: "predictions",
    label: "Predict",
    icon: ChartLineUp,
    color: "text-emerald-400",
  },
  {
    key: "driver_behavior",
    label: "Driver",
    icon: Truck,
    color: "text-violet-400",
  },
];

export default function AIInsightsPanel({ datasetId }) {
  const [activeType, setActiveType] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  const runAnalysis = async (type) => {
    setActiveType(type);
    setLoading(true);
    setInsight(null);
    try {
      const res = await axios.post(`${API}/analytics/ai-analysis`, {
        dataset_id: datasetId,
        analysis_type: type,
      });
      setInsight(res.data.insight);
    } catch (e) {
      toast.error("AI analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      data-testid="ai-insights-panel"
      className="bg-black border border-teal-900/50 rounded-sm p-4 h-full flex flex-col min-h-[420px]"
    >
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
        AI Agent
      </h3>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {analysisTypes.map(({ key, label, icon: Icon, color }) => (
          <Button
            key={key}
            variant="outline"
            size="sm"
            onClick={() => runAnalysis(key)}
            disabled={loading}
            className={`border-zinc-800 hover:bg-zinc-800 text-xs h-8 transition-colors duration-200 ${
              activeType === key ? "bg-zinc-800 border-teal-800" : ""
            }`}
            data-testid={`ai-${key}-btn`}
          >
            <Icon
              size={14}
              weight="duotone"
              className={`mr-1 shrink-0 ${color}`}
            />
            {label}
          </Button>
        ))}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <CircleNotch
              size={24}
              className="text-teal-400 animate-spin mb-2"
            />
            <span className="text-xs text-zinc-400 font-mono">
              Analyzing...
            </span>
          </div>
        ) : insight ? (
          <div
            className="text-xs text-zinc-300 font-mono leading-relaxed whitespace-pre-wrap pr-2"
            data-testid="ai-insight-content"
          >
            {insight.content}
          </div>
        ) : (
          <div className="text-center py-12">
            <Brain
              size={36}
              weight="duotone"
              className="text-zinc-700 mx-auto mb-3"
            />
            <p className="text-xs text-zinc-600">
              Select an analysis type to begin
            </p>
            <p className="text-[10px] text-zinc-700 mt-1">
              Powered by GPT-5.2
            </p>
          </div>
        )}
      </ScrollArea>
    </Card>
  );
}
